import { ethers } from 'ethers';
import { formatEther, parseEther, parseUnits } from 'ethers/lib/utils';
import { useWeb3React } from '@web3-react/core';
import { config } from '../../../config/config';
import useTradeState from '../../../context/trade/useTradeState';
import { leverageMultiplier } from '../../../helpers';
import ExchangeSwapContent from './ExchangeSwapContent';
import useReferralState from '../../../context/referral/useReferralState';
import useTokenDetails from '../../../hooks/web3/useTokenDetails';
import { EnabledChains, OrderBookType, Validators } from '../../../config/constants';
import useChartState from '../../../context/chart/useChartState';
import useCreateOrder from '../../../hooks/web3/trade/useCreateOrder';

const ExchangeMarket: React.FC = () => {
    const { account, chainId } = useWeb3React();
    const { referral } = useReferralState();
    const { collateral, leverage, isShort } = useTradeState();
    const { selectedTradePair } = useChartState();
    const createOrder = useCreateOrder();

    const { decimals } = useTokenDetails(
        config.addresses[chainId as EnabledChains[number]]?.collateral
    );

    const validators: Validators[] = [];

    const getBankruptcyPrice = (entryPrice: string) => {
        return formatEther(
            parseEther(entryPrice)
                .mul(
                    parseEther('1')[isShort ? 'add' : 'sub'](
                        parseEther('1')
                            .mul(parseUnits('1', 2))
                            .div(parseUnits(leverage, 2))
                    )
                )
                .div(parseEther('1'))
        );
    };

    const handleConfirm = async () => {
        if (!selectedTradePair) throw new Error('No Pool selected');

        const referrer =
            !referral?.address ||
            referral?.address.toLowerCase() === account?.toLowerCase()
                ? ethers.constants.AddressZero
                : referral?.address;

        await createOrder(
            OrderBookType.OPEN,
            {
                tradePair: selectedTradePair.address,
                margin: parseUnits(collateral, decimals).toString(),
                leverage: leverageMultiplier(leverage).toString(),
                isShort,
                referrer: referrer.toLowerCase(),
                whitelabelAddress: ethers.constants.AddressZero,
            },
            'OpenPositionOrder',
            isShort,
            getBankruptcyPrice,
            { isMarketOrder: true, tradePair: selectedTradePair }
        );
    };

    return <ExchangeSwapContent handleConfirm={handleConfirm} validators={validators} />;
};

export default ExchangeMarket;
